import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { BarChart3, QrCode, Link2, Layers, MapPin, ChevronRight, Menu, MessageCircle, Truck, Target, } from 'lucide-react';
import { motion, stagger } from 'framer-motion';
const SmartLinkFlow = React.lazy(() => import('@/routes/_layout/how-it-works/-components/smart-link-flow').then((m) => ({
    default: m.SmartLinkFlow,
})));
export const Route = createFileRoute('/_layout/how-it-works/')({
    component: HowItWorksPage,
    head: () => ({
        meta: [
            { title: 'How Adora Works — From Scan to Analytics' },
            {
                name: 'description',
                content: 'Create a Smart Link, attach a campaign, distribute offline materials, and see exactly where people come from and what they click next.',
            },
            { property: 'og:title', content: 'How Adora Works — From Scan to Analytics' },
            {
                property: 'og:description',
                content: 'Create a Smart Link, attach a campaign, distribute offline materials, and see exactly where people come from and what they click next.',
            },
        ],
    }),
});
const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    visible: { opacity: 1, y: 0 },
};
function HowItWorksPage() {
    return (<div className="min-h-dvh bg-background">
            <motion.section className="mx-auto max-w-6xl px-6 md:px-8 py-12 md:py-16" initial="hidden" animate="visible" variants={{ visible: { transition: { delayChildren: stagger(0.08) } } }}>
                <motion.h1 className="text-3xl md:text-5xl font-bold leading-tight" variants={fadeUp}>
                    How Adora works
                </motion.h1>

                <motion.p className="mt-4 max-w-2xl text-muted-foreground" variants={fadeUp}>
                    Adora helps you measure what happens <strong>after</strong> people scan a QR code or click a link —
                    especially in the real world. You don’t just track scans. You track <strong>intent</strong>.
                </motion.p>

                <motion.div className="mt-6 flex flex-col sm:flex-row gap-2" variants={fadeUp}>
                    <Button asChild size="lg">
                        <Link to="/sign-up" search={{ redirect: '/console' }}>
                            Start free
                        </Link>
                    </Button>
                    <Button asChild size="lg" variant="outline">
                        <Link to="/pricing">View pricing</Link>
                    </Button>
                </motion.div>

                <motion.div className="mt-8" variants={fadeUp}>
                    <Card>
                        <CardContent className="p-5 md:p-6">
                            <div className="text-sm text-muted-foreground">
                                <div className="font-medium text-foreground mb-2">The simple model</div>
                                <div className="flex flex-wrap items-center gap-2">
                                    <Pill icon={<Link2 className="h-4 w-4"/>}>Entry Point (Smart Link)</Pill>
                                    <Arrow />
                                    <Pill icon={<Target className="h-4 w-4"/>}>Campaign</Pill>
                                    <Arrow />
                                    <Pill icon={<Layers className="h-4 w-4"/>}>Destinations</Pill>
                                    <Arrow />
                                    <Pill icon={<BarChart3 className="h-4 w-4"/>}>Analytics</Pill>
                                </div>
                                <div className="mt-3">
                                    A <strong>Campaign</strong> is what you measure. A <strong>Smart Link</strong> is
                                    how people enter — and it can open a campaign landing page or go straight to a
                                    destination.
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>
            </motion.section>

            <Separator />

            <motion.section className="mx-auto max-w-6xl px-6 md:px-8 py-12" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.25 }} variants={{ visible: { transition: { delayChildren: stagger(0.1) } } }}>
                <motion.h2 className="text-2xl md:text-3xl font-semibold" variants={fadeUp}>
                    1. Create an entry point (Smart Link)
                </motion.h2>

                <motion.p className="mt-2 max-w-2xl text-muted-foreground" variants={fadeUp}>
                    A Smart Link is your <strong>entry point</strong>. It’s what people scan or click — and it is where
                    Adora starts tracking.
                </motion.p>

                <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <StepCard icon={<QrCode />} title="QR code included" text="Use it on printed materials, packaging, menus, or signage."/>
                    <StepCard icon={<Link2 />} title="Short link included" text="Use it for social media, bio links, messaging, or WhatsApp/Telegram."/>
                    <StepCard icon={<Target />} title="Choose the target" text="Send people to a campaign landing page or directly to a destination."/>
                </div>

                <div className="mt-6 text-sm text-muted-foreground">
                    Learn more:{' '}
                    <Link to="/smart-link" className="text-primary hover:underline">
                        What is a Smart Link?
                    </Link>
                </div>
            </motion.section>

            <Separator />

            <motion.section className="mx-auto max-w-6xl px-6 md:px-8 py-12" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.25 }} variants={{ visible: { transition: { delayChildren: stagger(0.1) } } }}>
                <motion.h2 className="text-2xl md:text-3xl font-semibold" variants={fadeUp}>
                    2. Offer intent options (what people actually want)
                </motion.h2>

                <motion.p className="mt-2 max-w-2xl text-muted-foreground" variants={fadeUp}>
                    Instead of forcing one destination, Adora lets people choose their intent — so you learn what they
                    wanted next.
                </motion.p>

                <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <StepCard icon={<Menu />} title="Menu" text="People check the menu or price list."/>
                    <StepCard icon={<MapPin />} title="Directions" text="People open maps and navigate."/>
                    <StepCard icon={<MessageCircle />} title="Message" text="People contact you on chat."/>
                    <StepCard icon={<Truck />} title="Delivery" text="People order from delivery platforms."/>
                </div>
            </motion.section>

            <Separator />

            <motion.section className="mx-auto max-w-6xl px-6 md:px-8 py-12" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} variants={{ visible: { transition: { delayChildren: stagger(0.08) } } }}>
                <motion.h2 className="text-2xl md:text-3xl font-semibold" variants={fadeUp}>
                    See the flow in 10 seconds
                </motion.h2>

                <motion.p className="mt-2 max-w-2xl text-muted-foreground" variants={fadeUp}>
                    One entry point → people choose intent → you see what works.
                </motion.p>

                <motion.div className="mt-6" variants={fadeUp}>
                    <React.Suspense fallback={<Card>
                                <CardContent className="p-6 text-sm text-muted-foreground">
                                    Loading the explainer…
                                </CardContent>
                            </Card>}>
                        <SmartLinkFlow variant="embed"/>
                    </React.Suspense>
                </motion.div>
            </motion.section>

            <Separator />

            <motion.section className="mx-auto max-w-6xl px-6 md:px-8 py-12" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.25 }} variants={{ visible: { transition: { delayChildren: stagger(0.1) } } }}>
                <motion.h2 className="text-2xl md:text-3xl font-semibold" variants={fadeUp}>
                    3. Track placement without changing your destination
                </motion.h2>

                <motion.p className="mt-2 max-w-2xl text-muted-foreground" variants={fadeUp}>
                    Smart Links let you measure <strong>where people came from</strong> — flyer vs packaging vs counter
                    vs tuk-tuk — while keeping the same destination.
                </motion.p>

                <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <StepCard icon={<MapPin />} title="Different placements" text="Packaging, flyer, counter, tuk-tuk — each can have its own entry point."/>
                    <StepCard icon={<Link2 />} title="Same destination" text="All can point to the same page, while tracking placement separately."/>
                    <StepCard icon={<BarChart3 />} title="Clear comparison" text="See which placement or idea actually performs better."/>
                </div>
            </motion.section>

            <Separator />

            <motion.section className="mx-auto max-w-6xl px-6 md:px-8 py-12" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.25 }} variants={fadeUp}>
                <Card>
                    <CardHeader>
                        <CardTitle>What’s next</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground space-y-2">
                        <p>
                            Adora is focused on clarity first. Comparisons already help you optimize real-world
                            promotions.
                        </p>
                        <p>
                            Over time, Adora will expand into more advanced experiments (automation, recommendations,
                            deeper segmentation).
                        </p>
                    </CardContent>
                </Card>
            </motion.section>

            <motion.section className="mx-auto max-w-6xl px-6 md:px-8 py-14" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} variants={fadeUp}>
                <Card>
                    <CardContent className="p-6 md:p-10 flex flex-col md:flex-row gap-6 justify-between">
                        <div>
                            <h3 className="text-xl md:text-2xl font-semibold">
                                Start measuring real-world campaigns today
                            </h3>
                            <p className="mt-2 text-muted-foreground max-w-xl">
                                No scan limits. No hidden complexity. Just clarity.
                            </p>
                        </div>

                        <div className="flex flex-col md:flex-row gap-2">
                            <Button asChild size="lg">
                                <Link to="/sign-up" search={{ redirect: '/console' }}>
                                    Create free account
                                </Link>
                            </Button>
                            <Button asChild size="lg" variant="outline">
                                <Link to="/compare">
                                    Compare Adora
                                    <ChevronRight className="ml-1.5 h-4 w-4"/>
                                </Link>
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </motion.section>
        </div>);
}
function Arrow() {
    return <span className="text-muted-foreground/60">→</span>;
}
function Pill({ icon, children }) {
    return (<span className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm bg-background">
            <span className="text-primary">{icon}</span>
            <span>{children}</span>
        </span>);
}
const StepCard = ({ icon, title, text }) => (<Card className="h-full">
        <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary">
                    {icon}
                </span>
                {title}
            </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">{text}</CardContent>
    </Card>);
StepCard.displayName = 'StepCard';
